import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "";

const eventLabel = (e) => ({
  LOGIN_SUCCESS: "Login riuscito",
  LOGIN_FAILED: "Login fallito",
  PASSWORD_CHANGED: "Password cambiata",
  PASSWORD_RESET_ADMIN: "Reset da SEDE",
  PASSWORD_RESET_REQUEST: "Richiesta recupero",
  PASSWORD_RESET_CONFIRM: "Conferma recupero",
}[e] || e || "—");

const riskClass = (r) => {
  if (r === "high") return "badge bg-danger";
  if (r === "medium") return "badge bg-warning text-dark";
  return "badge bg-success";
};

const riskLabel = (r) => {
  if (r === "high") return "Alto";
  if (r === "medium") return "Medio";
  return "Basso";
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("it-IT");
};

export default function SecurityAudit() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [event, setEvent] = useState("");
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const unlockFromLog = async (row) => {
    const username = row.username || row.target_user || row.actor;

    if (!username) {
      alert("Username non disponibile.");
      return;
    }

    if (!window.confirm(`Sbloccare l'utente ${username}?`)) return;

    try {
      const token = localStorage.getItem("access");
      const res = await fetch("/api/security/unblock-user/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ username }),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.detail || "Errore sblocco utente");
        return;
      }

      alert(`Utente ${data.username} sbloccato.`);
      load();
    } catch (e) {
      alert("Errore rete durante lo sblocco utente.");
    }
  };

  const resetFromLog = async (row) => {
    const label = row.username || row.email || "questo utente";

    if (!window.confirm(`Inviare una nuova password provvisoria a ${label}?`)) return;

    try {
      const token = localStorage.getItem("access");
      const res = await fetch("/api/users/reset-from-log/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          username: row.username,
          email: row.email,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.detail || "Errore reset password");
        return;
      }

      if (data.email_sent) {
        alert(`Email di reset inviata a ${data.email_to}.`);
      } else {
        alert(`Password provvisoria generata per ${data.username}, ma l'utente non ha email associata.`);
      }

      load();
    } catch (e) {
      alert("Errore rete durante il reset password.");
    }
  };

const load = async () => {
    setLoading(true);
    setErr("");

    try {
      const params = new URLSearchParams();
      params.set("limit", "100");
      if (event) params.set("event", event);
      if (q.trim()) params.set("q", q.trim());

      const token = localStorage.getItem("access");
      const res = await fetch(`${API_URL}/api/security/audit-logs/?${params.toString()}`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Impossibile caricare audit log.");

      setRows(data.results || []);
      setCount(data.count || 0);
    } catch (e) {
      setRows([]);
      setCount(0);
      setErr(e.message || "Errore imprevisto.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h2 className="m-0">🔒 Sicurezza</h2>
          <div className="text-muted">Audit log accessi e password</div>
        </div>
        <div className="d-flex gap-2">
          <Link to="/" className="btn btn-outline-secondary">⟵ Menu principale</Link>
          <button className="btn btn-outline-primary" onClick={load} disabled={loading}>
            {loading ? "Caricamento..." : "Aggiorna"}
          </button>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12 col-md-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="text-muted small">Eventi totali</div>
              <div className="h3 mb-0">{count}</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="text-muted small">Rischio alto</div>
              <div className="h3 mb-0 text-danger">{rows.filter((r) => r.risk === "high").length}</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="text-muted small">Password / recuperi</div>
              <div className="h3 mb-0">{rows.filter((r) => (r.event || "").includes("PASSWORD")).length}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-4">
              <label className="form-label small text-muted">Evento</label>
              <select className="form-select" value={event} onChange={(e) => setEvent(e.target.value)}>
                <option value="">Tutti</option>
                <option value="LOGIN_SUCCESS">Login riuscito</option>
                <option value="LOGIN_FAILED">Login fallito</option>
                <option value="PASSWORD_CHANGED">Password cambiata</option>
                <option value="PASSWORD_RESET_ADMIN">Reset da SEDE</option>
                <option value="PASSWORD_RESET_REQUEST">Richiesta recupero</option>
                <option value="PASSWORD_RESET_CONFIRM">Conferma recupero</option>
              </select>
            </div>
            <div className="col-12 col-md-5">
              <label className="form-label small text-muted">Cerca</label>
              <input
                className="form-control"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="username, email, dettaglio"
              />
            </div>
            <div className="col-12 col-md-3 d-flex gap-2">
              <button className="btn btn-primary w-100" onClick={load}>Applica</button>
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setEvent("");
                  setQ("");
                  setTimeout(load, 0);
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      <div className="card shadow-sm">
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Data</th>
                <th>Evento</th>
                <th>Rischio</th>
                <th>Utente</th>
                <th>Email</th>
                <th>IP</th>
                <th>Dettaglio</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-4">Caricamento…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-4">Nessun evento trovato</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className={r.risk === "high" ? "table-danger" : ""}>
                  <td style={{ whiteSpace: "nowrap" }}>{fmtDate(r.created_at)}</td>
                  <td><strong>{eventLabel(r.event)}</strong></td>
                  <td><span className={riskClass(r.risk)}>{riskLabel(r.risk)}</span></td>
                  <td>
                    <div>{r.username || r.target_user || r.actor || "—"}</div>
                    {r.is_blocked && (
                      <div className="mt-1 d-flex flex-wrap gap-2 align-items-center">
                        <span className="badge bg-danger">BLOCCATO</span>
                        <button
                          className="btn btn-sm btn-outline-success py-0 px-2"
                          onClick={() => unlockFromLog(r)}
                          title="Sblocca subito l'utente senza attendere la scadenza automatica"
                        >
                          Sblocca
                        </button>
                      </div>
                    )}
                  </td>
                  <td>{r.email || "—"}</td>
                  <td>{r.ip_address || "—"}</td>
                  <td style={{ maxWidth: 360 }}>
                    <div>{r.detail || "—"}</div>
                    {["LOGIN_FAILED", "PASSWORD_RESET_REQUEST"].includes(r.event) && (r.username || r.email) && (
                      <button
                        className="btn btn-sm btn-outline-danger mt-2"
                        onClick={() => resetFromLog(r)}
                        title="Genera nuova password provvisoria e invia email all'utente"
                      >
                        Invia email reset
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
